// Per spec/blocks.md §6. Text-only grid of member entries. Used for
// "Welcome New Members" and any future variant ("Member Spotlights",
// "Member Promotions", etc.) — Block ID generalized from
// `welcome-new-members` → `member-list` at P-037.16 D-116. Auto-hides on
// empty list.
//
// **P-037.16 D-116 redesign:**
// - Fixed 3-column layout (chapter convention).
// - Per-member fields: `name` + `company` + `location` (required) +
//   `url` (optional anchor on the name) + `partnerName` (optional).
//   MemberCard renders text-only — no photo.
// - **P-037.29 D-137** — optional `body` richText field DROPPED per
//   Banks's "only `text` + `time-location-cost` carry richText" rule.
//   Staff composes a separate `text` Block above member-list when
//   they want intro copy. Schema v10 → v11 migration is a Zod-strip
//   pass-through.
//
// **P-037.17 D-117 refinements:**
// - **3-col rhythm** (system-wide): `30 / 190 / 25 / 190 / 25 / 190 / 30`
//   px. Inner content area 620 px split into three 190 px card cells +
//   two 25 px spacer cells.
// - **Per-row dividers split into 3 segments** matching the column cells,
//   with gaps where the gutters are. Empty positions in odd-count last
//   rows still get a divider segment above (visual continuity).
// - **20 px gap** above and below the divider rule (system-wide 3-col
//   convention).
// - **34 px body top-padding** → 50 px heading↔body (D-117).
//
// **P-037.18 D-118 refactor:**
// - Per-row divider implementation extracted to the shared `gridLayout`
//   helper (`{ rowDivider: { color, padY } }` opt). The previous
//   hand-rolled `rowDividerHtml` + `renderGrid` functions are gone;
//   day-chairs now consumes the same helper with the same convention.
// - Divider color promoted: inline `#e5e5e5` → `tokens.color['rule-
//   subtle']` (D-118 promotion).
// - MemberCard receives `linkColor` from `ctx.linkColor` (template-
//   level link color derived from Header style). Replaces the
//   pre-D-118 hard-coded `#c69b34` member-card NAME_LINK_COLOR.
//
// Schema v2 → v3 migration handles persisted welcome-new-members instances
// (D-116). v3 → v4 (D-118) is a no-op for member-list specifically.

import { z } from 'zod'
import { defineBlock } from '../render/define-block'
import { html } from '../render'
import { tokens } from '../tokens'
import { memberCard, type MemberCardProps } from '../components/member-card'
import { gridLayout, pickDefined } from '../components/helpers'

const memberSchema = z.object({
  name: z.string().min(1),
  company: z.string().min(1),
  location: z.string().min(1),
  url: z.string().url().optional(),
  partnerName: z.string().min(1).optional(),
})

type Member = z.infer<typeof memberSchema>

const optionsSchema = z.object({})

const inputsSchema = z.object({
  members: z.array(memberSchema),
})

function memberToProps(item: Member, linkColor: string): MemberCardProps {
  return {
    name: item.name,
    company: item.company,
    location: item.location,
    linkColor,
    ...pickDefined({ url: item.url, partnerName: item.partnerName }),
  }
}

export const memberList = defineBlock({
  id: 'member-list',
  label: 'Member List',
  category: 'content',
  optionsSchema,
  inputsSchema,
  defaultOptions: {},
  defaultInputs: {
    members: [],
  },
  autoHide: ({ inputs }) => inputs.members.length === 0,
  render: ({ inputs }, ctx) => {
    // P-037.18 D-118: per-row divider via the shared gridLayout opt
    // (replaces the prior hand-rolled `renderGrid` + `rowDividerHtml`).
    // Same 3-segment divider pattern, same 20 px padY — now centralized
    // for day-chairs to consume the identical idiom.
    const gridHtml = gridLayout(
      inputs.members,
      3,
      (m) => memberCard(memberToProps(m, ctx.linkColor)),
      { rowDivider: { color: tokens.color['rule-subtle'], padY: 20 } },
    )
    // P-037.29 D-137: the optional `body` richText row was dropped —
    // users compose a separate `text` Block above member-list when
    // they want intro copy.
    // prettier-ignore
    return html`<table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%"><tr><td style="padding: 0 30px;">${gridHtml}</td></tr></table>`
  },
})
